import { useAppStore } from "@/stores/app";
import { computed } from "vue";

type Asset = {
  src: string;
  alt: string;
  public?: boolean;
};

type TutorialStep = Asset & {
  order: number;
};

type Favicon = {
  src: string;
  size?: string;
  public?: boolean;
};

type Favicons = {
  icon32: Favicon;
  icon96: Favicon;
  icon144: Favicon;
  icon180: Favicon;
  icon192: Favicon;
  icon270: Favicon;
  icon512: Favicon;
};

type BrandAssets = {
  insuranceLogo: Asset;
  desktopBackground: Asset;
  tutorial: TutorialStep[];
  favicons: Favicons;
};

const S3_BASE_URL = import.meta.env.VITE_APP_S3_PUBLIC_URL;

const assetsConfig: Omit<BrandAssets, "tutorial"> = {
  insuranceLogo: {
    src: "seguro-logo.png",
    alt: "Seguro Logo",
  },
  desktopBackground: {
    src: "desktop-bg.jpg",
    alt: "Background Desktop",
  },
  favicons: {
    icon32: { src: "icon-32.png", size: "32x32", public: true },
    icon96: { src: "icon-96.png", size: "96x96", public: true },
    icon144: { src: "icon-144.png", size: "144x144", public: true },
    icon180: { src: "icon-180.png", size: "180x180", public: true },
    icon192: { src: "icon-192.png", size: "192x192", public: true },
    icon270: { src: "icon-270.png", size: "270x270", public: true },
    icon512: { src: "icon-512.png", size: "512x512", public: true },
  },
};

function buildTutorialSteps(brand: string, count: number): TutorialStep[] {
  const steps: TutorialStep[] = [];

  for (let i = 1; i <= count; i++) {
    steps.push({
      src: `${S3_BASE_URL}/${brand}/assets/step-${i}.png`,
      alt: `Tutorial Step ${i}`,
      order: i,
    });
  }

  return steps;
}

function processAsset(brand: string, asset: any): any {
  if (typeof asset !== "object") throw new Error("Asset não é um objeto");

  const isSimpleAsset = asset.src;

  if (isSimpleAsset) {
    const src = `${S3_BASE_URL}/${brand}/assets/${asset.src}`;
    return { ...asset, src };
  } else {
    const processed: any = {};
    for (const key in asset) {
      processed[key] = processAsset(brand, asset[key]);
    }
    return processed;
  }
}

export const useAssets = () => {
  const appStore = useAppStore();
  const brand = appStore.brand;
  const numberOfTutorialSteps = appStore.numberOfTutorialSteps;

  const brandAssets = computed(() => {
    const brandConfig = assetsConfig;
    if (!brandConfig) {
      throw new Error(`Não há imagens cadastradas para a marca ${brand}`);
    }

    const processedAssets = processAsset(brand, {
      insuranceLogo: brandConfig.insuranceLogo,
      desktopBackground: brandConfig.desktopBackground,
      favicons: brandConfig.favicons,
    });

    return {
      ...processedAssets,
      tutorial: buildTutorialSteps(brand, numberOfTutorialSteps),
    } as BrandAssets;
  });

  const favicons = computed(() => brandAssets.value.favicons);

  return {
    brandAssets,
    favicons,
  };
};
